import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * px per viewBox unit of a responsive stage SVG (width 100%, height auto).
 * Returns both a state value (for things that need a re-render, like the
 * character's px size) and a ref (for the per-frame driver, which must not
 * re-render anything).
 */
export function useStageScale(ref: RefObject<SVGSVGElement | null>, vbWidth: number) {
  const [scale, setScale] = useState(1);
  const scaleRef = useRef(1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const s = el.clientWidth / vbWidth || 1;
      scaleRef.current = s;
      setScale((prev) => (Math.abs(prev - s) < 0.001 ? prev : s));
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, vbWidth]);
  return { scale, scaleRef };
}

/** True while the viewport is at most `px` wide. Live-updates. */
export function useMediaMax(px: number): boolean {
  const query = `(max-width: ${px}px)`;
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [query]);
  return matches;
}

/**
 * A smooth open path through `points` (Catmull-Rom converted to cubic Beziers),
 * as an SVG `d` string. `tension` 0 is a straight polyline, 1 the classic curve.
 */
export function splineThrough(points: ReadonlyArray<{ x: number; y: number }>, tension = 0.9): string {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  const k = tension / 6;
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    const c1x = p1.x + (p2.x - p0.x) * k;
    const c1y = p1.y + (p2.y - p0.y) * k;
    const c2x = p2.x - (p3.x - p1.x) * k;
    const c2y = p2.y - (p3.y - p1.y) * k;
    d += ` C ${r(c1x)} ${r(c1y)}, ${r(c2x)} ${r(c2y)}, ${p2.x} ${p2.y}`;
  }
  return d;
}

const r = (v: number) => Math.round(v * 10) / 10;

/** Path length nearest to a point, by sampling every `step` units. */
export function lengthNearest(path: { getTotalLength(): number; getPointAtLength(l: number): { x: number; y: number } }, pt: { x: number; y: number }, step = 2): number {
  const total = path.getTotalLength();
  let best = 0;
  let bestD = Infinity;
  for (let l = 0; l <= total; l += step) {
    const p = path.getPointAtLength(l);
    const d = (p.x - pt.x) ** 2 + (p.y - pt.y) ** 2;
    if (d < bestD) {
      bestD = d;
      best = l;
    }
  }
  return best;
}
